import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import type { Rng } from '../core/rng';
import type { BranchOption, StatusApply, WeaponDef, WeaponStats } from '../core/types';
import { destroyFrames, type FxFrames } from '../core/pixelfx';
import { auraFrames, giantSword, orbitFrames, orbitKeepsUpright, swingFrames, type BurstStyle } from '../core/skillfx';
import type { PlayerStats } from './stats';
import { branchOptions, computeWeaponStats, computeWeaponStatus, forkOptions, nextBranch, nextLevelDesc } from './stats';
import type { Enemy } from './entities';
import { audio, type PlayOpts, type Sfx } from '../core/audio';
import { lookOf, rampOf, type Look } from './looks';
import { applyFrame, ELEMENTS, SpiritCourt, spiritFrames, type ArtFrame } from './spirits';
import { TempestStorm } from './tempest';

/**
 * 무기가 게임 본체에 요구하는 것들.
 * game.ts가 이 인터페이스를 구현한다(순환 import 방지).
 */
/** 탄 하나를 쏠 때 필요한 것 전부. 인자가 많아 객체로 받는다. */
export interface ProjectileSpec {
  x: number;
  y: number;
  vx: number;
  vy: number;
  damage: number;
  pierce: number;
  radius: number;
  knockback: number;
  life: number;
  def: WeaponDef;
  status: StatusApply;
  /** 사라질 때 터지는 반경. 0이면 안 터진다. */
  explodeRadius?: number;
  explodeDamage?: number;
  /** 좌우 반폭. 0이 아니면 둥근 탄이 아니라 진행 방향에 가로로 누운 띠로 맞는다(파도). */
  halfWidth?: number;
  /** 하늘에서 떨어진다 — 날아가는 동안은 안 맞고 life 가 다하면 그 자리가 터진다 */
  fall?: boolean;
  /** 터진 자리에 불이 남는다 */
  pool?: boolean;
  /** 맞힐 때마다 입힌 피해의 이 비율만큼 체력을 되찾는다(넘실이는 파도) */
  lifesteal?: number;
}

/** 장판에 붙는 것들 */
export interface FieldOpts {
  /** 사는 동안 플레이어를 따라다닌다 */
  follow?: boolean;
  /** 안에 있으면 받는 피해가 이만큼 준다 (0~1) */
  guard?: number;
  /** 이 간격(초)마다 영역 안 가장 센 적에게 황금 광선 */
  beam?: number;
  beamDamage?: number;
  /** 안에 있으면 초당 최대 체력의 이 비율만큼 회복 */
  heal?: number;
}

/** 도약 (사냥 준비). 이동 방향으로 뛰어들며 지나간 길을 할퀴고, 착지 자리를 터뜨린다. */
export interface LeapSpec {
  /** 정규화된 방향 */
  dirX: number;
  dirY: number;
  distance: number;
  /** 착지 폭발 반경 */
  radius: number;
  damage: number;
  knockback: number;
  status: StatusApply;
  attr: string;
  burst: BurstStyle;
  ramp: readonly string[];
  /** 착지 소리 */
  burstSfx?: Sfx;
  /** 착지했을 때 — 착지로 잡은 수를 알려 준다(연속 사냥 쿨타임 감소) */
  onLand?: (kills: number) => void;
}

/** 전류 한 줄기. */
export interface ChainSpec {
  x: number;
  y: number;
  /** 첫 대상을 찾는 거리 (시전자 기준) */
  firstRange: number;
  /** 옮겨붙는 거리 */
  jumpRange: number;
  damage: number;
  jumps: number;
  knockback: number;
  status: StatusApply;
  /** 튄 자리마다 퍼지는 반경. 0이면 한 마리씩만 */
  splash: number;
  /** 튀는 선의 모양 (전류 = 굵은 노란 번개, 감전 = 가늘게 떠는 보랏빛 정전기, violet = 번개의 정령왕 — 굵은 보랏빛 번개) */
  style: 'arc' | 'static' | 'violet';
  splashStyle: BurstStyle;
  splashRamp?: readonly string[];
  /** 이미 맞은 적. 여러 줄기가 같은 적을 두 번 잡지 않게 서로 나눠 쓴다. */
  exclude: Set<number>;
  /** 번개 굵기 배율 (1 = 보통) */
  bolt: number;
}

/** 번개 한 줄기 (여제의 명령). 시작점에서 dir 방향으로 length 만큼, 폭 halfWidth*2 의 띠. */
export interface StrikeSpec {
  x: number;
  y: number;
  dirX: number;
  dirY: number;
  length: number;
  halfWidth: number;
  damage: number;
  knockback: number;
  status: StatusApply;
  /** 0보다 크면 번개 자리에 이 시간(초) 전기장이 남는다 */
  storm?: number;
}

export interface WeaponHost {
  readonly px: number;
  readonly py: number;
  readonly faceX: number;
  readonly faceY: number;
  readonly playerStats: PlayerStats;
  readonly rng: Rng;
  readonly fxLayer: Container;
  /** 캐릭터와 앞뒤를 가려 그리는 층 (zIndex = 발 y). 정령왕이 여기 선다 */
  readonly actorLayer: Container;
  /** 바닥 층 (적보다 아래) — 그림자 */
  readonly groundLayer: Container;
  /** 시뮬레이터·측정용: 액티브 스킬을 사람 대신 알아서 쓴다 */
  readonly autoActive: boolean;
  /** 도약하는 중인가 */
  readonly dashing: boolean;
  /** 변신하거나 풀리는 연출 중인가 (회광반조 — 이 동안은 새로 휘두르지 않는다) */
  readonly transforming: boolean;
  /** 지금까지 잡은 수 */
  readonly kills: number;

  spawnProjectile(spec: ProjectileSpec): void;

  spawnField(
    x: number, y: number, radius: number, damage: number,
    duration: number, slow: number, def: WeaponDef, status: StatusApply, opts?: FieldOpts,
  ): void;

  /** 바닥에 잠깐 타오르는 잿불 자국 (회광반조·불길 도배·불바다) */
  spawnCinder(x: number, y: number, radius: number, damage: number, status: StatusApply): void;

  /** 반경 안의 경험치 보석을 끌어온다 */
  vacuumGems(x: number, y: number, radius: number): void;

  /** 반경 안의 적 탄을 흩어 없앤다. reflectDamage 가 0보다 크면 없애는 대신 되돌려 보낸다. 건드린 수 */
  catchShots(x: number, y: number, radius: number, reflectDamage: number): number;

  /** 반경 안의 서로 다른 적을 n 마리까지 (가까운 순이 아니라 고르게) */
  pickTargets(x: number, y: number, radius: number, n: number): Enemy[];

  /** 무거운 한 방 — 화면이 아주 잠깐 멈춘다(자주 걸리지 않게 막아 둔다) */
  heavyHit(): void;

  /**
   * 회광반조 검상: (x, y) 를 중심으로 반경 radius 의 호(a0 → a1)를 따라 바닥에 상처가 난다.
   * reveal 초에 걸쳐 a0 쪽부터 "휘이익" 드러나고, 잠깐 남아 타오르며 닿은 적에게 damage 와 status 를 준다.
   */
  slashWound(x: number, y: number, radius: number, a0: number, a1: number, reveal: number, damage: number, status: StatusApply): void;

  /** 화면 흔들기 */
  shakeScreen(time: number, mag: number): void;

  /** 이번 프레임 몸 크기 배율 (타이탄) */
  setBodyScale(k: number): void;

  /** 넘치는 회복을 보호막으로 받는다 (세포 역설). cap 은 최대 체력 비율, 깨질 때 burstDamage 로 터진다 */
  setOverheal(cap: number, burstDamage: number, status: StatusApply): void;

  /** 도약한다(짧게 날아가는 동안 무적). 이미 도약 중이면 false. */
  leap(spec: LeapSpec): boolean;

  /**
   * 원형 범위 피해. 맞은 수를 돌려준다.
   * slot >= 0 이면 같은 적을 reHit 초 안에 다시 때리지 않는다(지속 타격용).
   */
  areaDamage(
    x: number, y: number, radius: number, damage: number, knockback: number,
    slowT: number, slowAmt: number, slot?: number, reHit?: number, status?: StatusApply,
  ): number;

  /** 부채꼴 범위 피해. */
  coneDamage(
    x: number, y: number, dirX: number, dirY: number,
    radius: number, arcDeg: number, damage: number, knockback: number,
    status?: StatusApply,
  ): number;

  /** 한 적에서 가까운 적으로 옮겨붙는 피해. 맞은 수를 돌려준다. */
  chainDamage(spec: ChainSpec): number;

  /** 번개가 한 번에 내리꽂힌다. 띠 안의 적 전부가 맞는다. 맞은 수를 돌려준다. */
  lightningStrike(spec: StrikeSpec): number;

  /**
   * 소한공: 앞으로 뻗는 띠(길이 length, 반폭 halfWidth) 안의 적을 친다. consume 이 0보다 크면 적마다 [서리]를 그만큼까지 녹여
   * 녹인 1 마다 피해 +bonus 배. 그다음 status 로 [서리]를 다시 입힌다. 한기 그림이 날아가고 끝에서 터진다. 맞은 수.
   */
  palmStrike(
    x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number,
    damage: number, knockback: number, status: StatusApply, consume: number, bonus: number, big: boolean,
  ): number;

  /**
   * 칼날이 a0 에서 a1 까지(라디안, 어느 쪽이 커도 된다) 쓸고 지나간 부채꼴 안의 적을 벤다.
   * seen 에 든 적은 건너뛰고, 벤 적은 seen 에 넣는다(한 번 휘두를 때 한 번만 맞게).
   */
  sweepDamage(
    x: number, y: number, a0: number, a1: number, radius: number,
    damage: number, knockback: number, status: StatusApply, seen: Set<number>,
  ): number;

  /** 장판 그림을 미리 굽는다(판이 멈춰 있을 때 — 레벨업 카드를 고른 순간) */
  warmField(def: WeaponDef, radius: number): void;

  /** 폭발 그림을 미리 굽는다(판이 멈춰 있을 때) */
  warmBurst(radius: number, attr: string, style: BurstStyle, ramp?: readonly string[]): void;

  /** radius 안의 적을 toRadius 근처까지 끌어당긴다(보스 제외). 끌려온 수를 돌려준다. */
  pullEnemies(x: number, y: number, radius: number, toRadius: number): number;

  /**
   * radius 안의 적을 공중으로 띄운다(광풍). dur 초 동안 height 까지 떠오르며 turns 바퀴 돌고, 그동안 못 움직인다.
   * 날아가는 쪽은 이미 받은 넉백 그대로다. 보스·엘리트는 무거워서 안 뜬다.
   * cd 초 안에 이미 뜬 적은 건너뛴다(0 이면 늘 띄운다 — 조화). 띄운 수를 돌려준다.
   */
  liftEnemies(x: number, y: number, radius: number, height: number, dur: number, turns: number, cd: number): number;

  nearestEnemy(x: number, y: number, maxRadius: number): Enemy | null;

  /** 반경 안에서 가장 센 적 — 보스 > 엘리트 > 체력 많은 적. 없으면 null */
  strongestEnemy(x: number, y: number, maxRadius: number): Enemy | null;

  /** 시작점에서 dir 방향 length 까지, 반폭 halfWidth 의 띠 안의 적을 전부 때린다(그림 없음). 맞은 수 */
  lineDamage(
    x: number, y: number, dirX: number, dirY: number, length: number, halfWidth: number,
    damage: number, knockback: number, status?: StatusApply,
  ): number;

  /** 화면 전체가 번쩍 */
  flashScreen(sec: number, alpha: number, tint: number): void;

  /** 화면 가운데 큰 띠 (정령왕이 나타날 때) */
  banner(text: string, sub: string, color: string): void;

  /** 적이 가장 두껍게 몰린 지점. 없으면 null. */
  densestSpot(x: number, y: number, maxRadius: number): { x: number; y: number } | null;

  /** 플레이어를 그 자리로 옮긴다. */
  teleport(x: number, y: number): void;

  /** 폭발 자국을 남긴다. delay 초 뒤에 나타나게 할 수 있다(충격파처럼 차례로 퍼지게). */
  burst(x: number, y: number, radius: number, attr: string, style?: BurstStyle, ramp?: readonly string[], delay?: number): void;

  /** 번개 선 하나 (그림만, 피해 없음). violet = 굵은 보랏빛 */
  boltLine(x0: number, y0: number, x1: number, y1: number, style: 'arc' | 'static' | 'violet', scale?: number, delay?: number): void;

  /** 실제 시간으로 잠깐 느리게 (판에는 영향 없다) */
  slowMotion(sec: number, scale: number): void;

  /** 지나간 자리에 잔상 줄을 긋는다(순간이동). */
  streak(x0: number, y0: number, x1: number, y1: number, color: number): void;

  /** 체력을 회복한다(최대치를 넘지 않는다). */
  healPlayer(amount: number): void;

  /** 캐릭터가 공격 동작을 취하게 한다. */
  playAttackPose(kind: 'attack' | 'cast'): void;
}

/**
 * 기사의 거대한 검 그림 (public/assets/fx/knight_sword — tools/build_fx.py --sword 가 받은 도트에서 만든다).
 * anchor 는 쥐는 자리(몸이 여기다), length 는 쥐는 자리 → 칼끝(px). 게임은 칼끝이 판정 반경 R 에 닿게 늘리고 줄인다.
 * 못 읽었으면 null — 그때는 코드로 찍은 검(giantSword)으로 대신한다.
 */
export interface SwordArt {
  texture: Texture;
  anchorX: number;
  anchorY: number;
  length: number;
  /** 쥐는 자리 → 코등이(px). 여기서부터 칼날 */
  guard: number;
}
/** 받은 그림으로 만든 검들: 'knight'(기사의 거대한 검) · 'ash'(회광반조 잿빛대검) */
const swordArts = new Map<string, SwordArt>();
export function setSwordArt(art: SwordArt | null, name = 'knight'): void {
  if (art) swordArts.set(name, art);
  else swordArts.delete(name);
}
export function getSwordArt(name = 'knight'): SwordArt | null {
  return swordArts.get(name) ?? null;
}

/** 캐릭터 특성의 "베기" (모션 기준) — 그 밖은 마법. 도약(사냥 준비)과 장풍(소한공)은 어느 쪽도 아니다 */
const SLASH_BEHAVIORS = new Set(['melee', 'greatblade', 'ashblade']);
/** 타이탄 충격파 그림을 구울 때 커진 정도를 맞추는 단위 */
const GROW_STEP = 0.05;
function growStep(grow: number): number {
  return 1 + Math.round((grow - 1) / GROW_STEP) * GROW_STEP;
}
/** 여러 번 베는 무기의 칼마다 바라보는 쪽에서 돌린 각도: 정면, 뒤, 좌, 우, 그리고 대각선 넷 */
const MELEE_DIRS = [0, Math.PI, Math.PI / 2, -Math.PI / 2, Math.PI / 4, -Math.PI / 4, (Math.PI * 3) / 4, (-Math.PI * 3) / 4];
const NEUTRAL_BEHAVIORS = new Set(['leap', 'palm']);

/** 소한공: 한 번 내지른 뒤 다음 손까지(초) */
const PALM_GAP = 0.22;

/** 회광반조: 한 번 휘두를 때 걸리는 시간과 다음 휘두르기까지의 틈(초) */
const ASH_SWEEP = 0.15;
const ASH_GAP = 0.07;
/** 회광반조 변신: 몸이 검붉게 물드는 시간(이 뒤에 둘레가 터진다 — game.ts 의 물들기와 같다) */
export const FORM_IGNITE = 0.3;
/** 기력이 바닥나 풀리면(지침) 이만큼은 회복이 멈춘다. 스스로 풀면 바로 회복한다 */
export const FORM_TIRED = 2;
/** 잔불(변신 안 한 동안): 쿨타임 배율 · 사거리 배율 */
const EMBER_CD = 2.2;
const EMBER_REACH = 0.85;

/** HUD 의 액티브 칸 하나. leap = 사냥 준비(횟수·충전), form = 회광반조(기력) */
export interface ActiveState {
  kind: 'leap' | 'form';
  /** 지금 쓸 수 있는 횟수 (변신은 기력이 가득이면 1) */
  charges: number;
  max: number;
  /** 다음 한 번이 차는 진행도 0~1 (변신은 기력) */
  ratio: number;
  /** 연속 사냥 단계 (0 = 아님) */
  combo: number;
  /** 변신 중인가 */
  on: boolean;
  /** 지쳐서 회복이 멈춰 있는가 */
  tired: boolean;
}

/** 한 번 휘두를 때마다 프레임을 넘겨 재생하는 이펙트. */
class SwingFx {
  private t = -1;
  readonly sprite: Sprite;

  constructor(
    layer: Container,
    private frames: FxFrames,
  ) {
    this.sprite = new Sprite(frames.textures[0]);
    this.sprite.anchor.set(frames.anchorX, frames.anchorY);
    this.sprite.alpha = frames.alpha ?? 1;
    this.sprite.visible = false;
    layer.addChild(this.sprite);
  }

  fire(rotation: number): void {
    this.t = 0;
    this.sprite.rotation = rotation;
    this.sprite.texture = this.frames.textures[0];
    this.sprite.visible = true;
  }

  update(dt: number, x: number, y: number): void {
    this.sprite.position.set(Math.round(x), Math.round(y));
    if (this.t < 0) return;
    this.t += dt;
    const i = Math.floor(this.t * (this.frames.fps ?? 17));
    if (i >= this.frames.textures.length) {
      this.t = -1;
      this.sprite.visible = false;
      return;
    }
    this.sprite.texture = this.frames.textures[i];
  }

  destroy(): void {
    this.sprite.destroy();
  }
}

/** 한 번에 여러 번 벨 때 뒤따르는 칼질 하나 */
interface PendingSwing {
  /** 남은 시간(초) */
  wait: number;
  dx: number;
  dy: number;
  /** 몇 번째 칼질인가 (0 = 첫 칼) */
  idx: number;
}

/**
 * 탄을 쏘는 무기가 노리는 거리(px). 1.11 에 460 → 540 으로 늘렸다(스킬 범위가 뱀서류보다 짧게 느껴진다는 요청).
 * 탄이 날아가는 시간도 1.15 → 1.3초(사방으로 쏘는 것은 1.4 → 1.6초)로 늘려 멀리까지 닿는다.
 */
const AIM_RANGE = 540;

/** 여러 번 벨 때 칼질 사이 간격(초). 이만큼 벌어져야 "두두!" 두 번으로 들린다. */
const SWING_GAP = 0.14;

/**
 * 여제의 명령: 시전 소리(콰르릉)가 시작되고 이만큼 뒤에 번개가 내리꽂힌다.
 * public/audio/sfx_royal.wav 의 "콰!" 가 이 시각에 오도록 음원을 만들었다(tools/build_audio.py).
 */
export const STRIKE_DELAY = 0.22;

/** 거대한 검이 한 번 휘두르는 동작 */
interface BladeSweep {
  /** 공연이 시작된 뒤 몇 초에 시작하나 */
  start: number;
  dur: number;
  from: number;
  to: number;
  /** 'slash' = 반 바퀴 남짓 크게 베기, 'spin' = 한 바퀴 회전 베기 */
  kind: 'slash' | 'spin';
}

export class WeaponInstance {
  level = 1;
  stats: WeaponStats;
  status: StatusApply;
  readonly look: Look;
  private timer = 0.15; // 판 시작하자마자 한 번 나가도록 짧게
  /** 이 무기가 거는 [화염] 한 중첩의 피해 = 지금 한 방 공격력 × 화염 배수. update 에서 매번 새로 잰다 */
  private burnDmg = 0;
  private orbitAngle = 0;

  // 동작별 시각 요소
  private nodes: Sprite[] = [];
  private nodeFrames: Texture[] = [];
  private nodeT = 0;
  private aura: Sprite | null = null;
  private auraFrames: FxFrames | null = null;
  private auraT = 0;
  private swings: SwingFx[] = [];
  private swingFrames: FxFrames | null = null;
  private pending: PendingSwing[] = [];
  /** 이번 연속 베기의 칼질 간격 */
  private volleyGap = 0;
  /** 궤도·오라의 맞는 소리 쉬는 시간 (쉬지 않고 때리므로 따로 늦춘다) */
  private hitRest = 0;
  /** 진화 갈림길에서 고른 갈래 id (갈림길을 만난 순서대로) */
  readonly picks: string[] = [];
  /** 여제의 명령: 소리가 먼저 나고 잠시 뒤 떨어질 번개들 */
  private strikes: Array<{ wait: number; dx: number; dy: number }> = [];
  // 기사: 거대한 검
  private blade: Sprite | null = null;
  private bladeTex: Texture | null = null;
  private trail: Graphics | null = null;
  /** 검 그림을 판정 반경에 맞추는 배율 (그림이면 R / length, 코드로 찍은 검이면 1) */
  private bladeK = 1;
  /** 빛 띠가 시작하는 반경 비율 (칼날이 시작하는 곳) */
  private bladeIn = 0.35;
  /** 공연(소환 → 끌어당김 → 휘두르기) 경과 시간. 음수면 쉬는 중 */
  private bladeT = -1;
  private bladePlan: BladeSweep[] = [];
  private bladeIdx = 0;
  private bladeAngle = 0;
  private bladeSeen = new Set<number>();
  private bladePulled = false;
  private trailPts: Array<{ a: number; t: number }> = [];
  /** 이펙트를 다시 구워야 하는지 판단하는 열쇠 */
  private fxKey = '';
  // 액티브(도약): 남은 횟수와 다음 한 번이 찰 때까지 남은 시간
  private charges = -1;
  private rechargeT = 0;
  private lastCd = 1;
  /** 연속 사냥: 착지한 뒤 남은 시간(이 안에 또 뛰면 연속)과 지금 몇 단째인가 */
  private comboT = 0;
  private combo = 0;
  // 타이탄: 가진 뒤로 잡은 수의 기준점, 다음 충격파까지
  private killBase = -1;
  private pulseT = 0;
  // 다중 소환 유지: 원소 정령 그림(공용 — 지우면 안 된다)과 정령왕 셋
  private nodeArt: ArtFrame[][] | null = null;
  private court: SpiritCourt | null = null;
  /** 광풍: 네 빛깔 회오리 */
  private storm: TempestStorm | null = null;
  // 회광반조 변신(Space): 기력 0~1, 변신 중인가, 지쳐서 회복이 멈춘 남은 시간, 변신 폭발까지 남은 시간
  private vigor = 1;
  private formOn = false;
  private tiredT = 0;
  private igniteT = -1;
  /** 처음 얻은 순간은 한 번 저절로 변신한다 — 무엇이 생겼는지 보여 주려고 */
  private formIntro = true;
  /** 마지막으로 풀린 까닭 (게임이 풀리는 연출을 고른다): tired = 기력이 바닥남, cancel = 스스로 풂 */
  formEnd: 'tired' | 'cancel' = 'cancel';
  /** 측정용: 'on' 이면 변신한 채 기력이 안 닳고, 'off' 면 변신하지 않는다 */
  formHold: 'on' | 'off' | null = null;

  constructor(
    readonly def: WeaponDef,
    /** 조합으로 나온 무기는 다시 조합 재료가 될 수 없다. */
    readonly isFusion = false,
  ) {
    this.stats = computeWeaponStats(def, 1);
    this.status = computeWeaponStatus(def, 1);
    this.look = lookOf(def);
  }

  get ramp(): readonly string[] {
    return rampOf(this.def);
  }

  /** 다음 레벨이 진화 갈림길(고를 갈래가 둘 이상)이면 그 갈래들 */
  get nextBranch(): BranchOption[] | null {
    return this.level >= this.def.maxLevel ? null : nextBranch(this.def, this.level, this.picks);
  }

  /** 다음 레벨이 갈림길인가 — 고를 것 없이 한 갈래만 남은 갈림길도 포함 (갈래 id 를 차례로 넘길 때 센다) */
  get atFork(): boolean {
    return this.level < this.def.maxLevel && !!this.def.levels[this.level - 1]?.choose?.length;
  }

  /** 다음 레벨업 카드 문구 */
  get nextDesc(): string {
    return nextLevelDesc(this.def, this.level, this.picks);
  }

  /** 지금까지 고른 갈래 이름들 */
  get pathNames(): string[] {
    const names: string[] = [];
    let b = 0;
    for (let i = 0; i < this.level - 1 && i < this.def.levels.length; i++) {
      const ch = this.def.levels[i].choose;
      if (!ch?.length) continue;
      const opts = branchOptions(ch, this.picks.slice(0, b));
      const id = this.picks[b++];
      names.push((opts.find((o) => o.id === id) ?? opts[0]).name);
    }
    return names;
  }

  /**
   * 레벨을 하나 올린다. 갈림길이면 pick(갈래 id)을 쓴다 — 없거나 지금 고를 수 없는 갈래면 고를 수 있는 첫 갈래.
   * 갈래가 하나뿐인 갈림길(앞 선택으로 정해진 것)도 그 갈래를 적어 둔다.
   */
  levelUp(pick?: string): void {
    if (this.level >= this.def.maxLevel) return;
    const opts = forkOptions(this.def, this.level, this.picks);
    if (opts) this.picks.push(opts.some((o) => o.id === pick) ? pick! : opts[0].id);
    this.level++;
    this.stats = computeWeaponStats(this.def, this.level, this.picks);
    this.status = computeWeaponStatus(this.def, this.level, this.picks);
    this.destroyVisuals();
  }

  private destroyVisuals(): void {
    for (const n of this.nodes) n.destroy();
    this.nodes = [];
    for (const t of this.nodeFrames) t.destroy(true);
    this.nodeFrames = [];
    this.nodeArt = null;
    this.court?.destroy();
    this.court = null;
    this.storm?.destroy();
    this.storm = null;
    this.aura?.destroy();
    this.aura = null;
    destroyFrames(this.auraFrames);
    this.auraFrames = null;
    for (const s of this.swings) s.destroy();
    this.swings = [];
    destroyFrames(this.swingFrames);
    this.swingFrames = null;
    this.pending = [];
    this.blade?.destroy();
    this.blade = null;
    this.bladeTex?.destroy(true);
    this.bladeTex = null;
    this.trail?.destroy();
    this.trail = null;
    this.bladeT = -1;
    this.fxKey = '';
  }

  destroy(): void {
    this.destroyVisuals();
  }

  /**
   * 이펙트 그림을 미리 굽는다 — 레벨업 카드를 고른 순간(판이 멈춰 있을 때) 부른다.
   * 예전에는 레벨업하면 그림을 버리고 다음 프레임에 새로 구워서, 레벨업 직후마다 10~26ms 멈칫했다.
   * 게임에는 아무 영향 없다: 나중에 update 가 어차피 할 굽기를 앞당길 뿐이다(같은 열쇠면 다시 안 굽는다).
   */
  prepareFx(host: WeaponHost): void {
    const s = this.stats;
    const area = s.area * host.playerStats.areaMul;
    switch (this.def.behavior) {
      case 'melee':
        this.ensureMeleeFx(host, s.radius * area, s.arc);
        break;
      case 'tempest':
        if (!TempestStorm.available()) this.ensureMeleeFx(host, s.radius * area, s.arc);
        break;
      case 'aura':
        this.ensureAuraFx(host, s.radius * area);
        break;
      case 'orbit':
        if (!(s.king > 0 && SpiritCourt.available())) this.ensureOrbitFx(host, s.size * area);
        break;
      default:
        break;
    }
    this.warmTextures(host);
  }

  /**
   * 무거운 그림(장판·폭발)만 굽는다 — 무기 그림을 안 만들어도 되므로, 레벨업 카드가 떠 있는 동안
   * "이 카드를 고르면 필요할 그림"을 미리 굽는 데도 쓴다(game.ts prewarmChoices).
   */
  warmTextures(host: WeaponHost): void {
    const s = this.stats;
    const area = s.area * host.playerStats.areaMul;
    // 장판 크기가 바뀌면(레벨업·범위 패시브) 그림을 새로 굽는다 — 자욱한 안개는 한 번에 최대 0.17초 걸렸다
    if (this.def.behavior === 'field') host.warmField(this.def, s.size * area);
    // 크기가 미리 정해지는 폭발(투사체가 터지는 자리·도약 착지·순간이동 착지)도 지금 굽는다.
    // 큰 폭발은 처음 한 번 굽는 데 0.1~0.3초(사냥 준비 착지 할퀴기 0.27초) 걸려 판 도중 멈칫했다
    const burst = this.look.burst ?? 'ring';
    const b = this.def.behavior;
    if ((b === 'projectile' || b === 'nova') && s.explodeRadius > 0) {
      host.warmBurst(s.explodeRadius * area, this.def.attr, burst, this.ramp);
    }
    if (b === 'projectile' && s.fall > 0) {
      host.warmBurst(Math.max(s.explodeRadius * area, s.size * area * 3, 36), this.def.attr, burst, this.ramp);
    }
    if (b === 'leap') {
      for (let step = 0; step <= 2; step++) host.warmBurst(s.radius * area * (1 + step * 0.15), this.def.attr, burst, this.ramp);
    }
    if (b === 'blink') host.warmBurst(s.radius * area, this.def.attr, burst, this.ramp);
    // 타이탄 충격파: 커지는 단계(5%)마다 한 장씩
    if (b === 'aura' && s.pulse > 0) {
      for (let g = 1; g <= 1 + s.grow + 1e-9; g += GROW_STEP) {
        host.warmBurst(s.radius * area * 1.35 * growStep(g), this.def.attr, 'air', this.ramp);
      }
    }
  }

  update(host: WeaponHost, dt: number, slot: number): void {
    // 1.24 전격 질주: 플레이어가 Shift 로 쓰는 대시다 — 저절로 나가지 않는다(쿨·횟수는 game.ts 가 센다)
    if (this.def.behavior === 'dash') return;
    const ps = host.playerStats;
    const s = this.stats;
    // 캐릭터 특성 (노아: 베기 +5%, 마법 -5%)
    const b = this.def.behavior;
    const typeMul = SLASH_BEHAVIORS.has(b) ? ps.slashMul : NEUTRAL_BEHAVIORS.has(b) ? 1 : ps.magicMul;
    const dmg = s.damage * ps.damageMul * typeMul;
    this.burnDmg = dmg * (this.def.burnMul ?? 0);
    const area = s.area * ps.areaMul;
    const cd = Math.max(0.05, s.cooldown * ps.cooldownMul);
    if (this.hitRest > 0) this.hitRest -= dt;
    // 광풍: 바람이 보석을 빨아들이고, 「폭풍의 눈」은 날아드는 적 탄을 흩는다
    if (s.vacuum > 0) host.vacuumGems(host.px, host.py, s.vacuum);
    if (s.deflect > 0) host.catchShots(host.px, host.py, s.radius * area, 0);

    switch (this.def.behavior) {
      case 'orbit':
        this.updateOrbit(host, dt, dmg, area, cd, slot);
        return;
      case 'aura':
        this.updateAura(host, dt, dmg, area, cd, slot);
        return;
      case 'melee':
        this.updateMelee(host, dt, dmg, area, cd);
        return;
      case 'tempest':
        if (TempestStorm.available()) this.updateTempest(host, dt, dmg, area, cd, slot);
        else this.updateMelee(host, dt, dmg, area, cd);
        return;
      case 'blink':
        this.updateBlink(host, dt, dmg, area, cd);
        return;
      case 'strike':
        this.updateStrike(host, dt, dmg, area, cd);
        return;
      case 'greatblade':
        this.updateGreatblade(host, dt, dmg, area, cd);
        return;
      case 'ashblade':
        this.updateAshblade(host, dt, dmg, area, cd);
        return;
      case 'palm':
        this.updatePalm(host, dt, dmg, area, cd);
        return;
      case 'leap':
        this.updateLeap(host, dt, cd);
        return;
      default:
        break;
    }

    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = cd;

    if (this.def.behavior === 'projectile') this.fireProjectiles(host, dmg, area, ps);
    else if (this.def.behavior === 'nova') this.fireNova(host, dmg, area, ps);
    else if (this.def.behavior === 'field') this.dropFields(host, dmg, area);
    else if (this.def.behavior === 'chain') this.fireChain(host, dmg, area);
  }

  /** 나갈 때 나는 소리 */
  private cast(opts?: PlayOpts): void {
    if (this.look.cast) audio.play(this.look.cast, { level: this.level, ...opts });
  }

  /**
   * 맞을 때 나는 소리. 한 번 휘둘러 40마리가 맞아도 한 번이다.
   * 종류마다 최소 간격이 있어서 쉬지 않고 때리는 궤도·오라도 드문드문만 난다.
   */
  private hit(host: WeaponHost): void {
    if (!this.look.hit) return;
    // 궤도·오라는 매 순간 무언가를 때리고 있다. 0.45초에 한 번이면 "닿고 있다"는 건 충분히 들린다.
    if (this.def.behavior === 'orbit' || this.def.behavior === 'aura' || this.def.behavior === 'tempest') {
      if (this.hitRest > 0) return;
      this.hitRest = 0.45;
    }
    audio.play(this.look.hit, { gain: this.look.hitGain ?? 1 });
    if (this.look.heavy) host.heavyHit();
  }

  /** 플레이어 스탯이 얹어진 최종 상태효과 */
  private outStatus(ps: PlayerStats): StatusApply {
    const st = this.status;
    const lock = this.stats.burnLock > 0 ? { burnLock: 3.5 } : null;
    if (!st.burn && !st.frost && !lock) return st;
    return {
      ...st,
      ...(lock ?? {}),
      burn: st.burn ? st.burn + ps.burnBonus : undefined,
      burnDmg: st.burn && this.burnDmg > 0 ? this.burnDmg : undefined,
      frost: st.frost ? st.frost + ps.frostBonus : undefined,
    };
  }

  /** 입힌 피해의 일부를 체력으로 돌려준다. */
  private drain(host: WeaponHost, hits: number, dmg: number): void {
    if (this.stats.lifesteal > 0 && hits > 0) {
      host.healPlayer(hits * dmg * this.stats.lifesteal);
    }
  }

  // 가장 가까운 적을 향해 발사
  private fireProjectiles(host: WeaponHost, dmg: number, area: number, ps: PlayerStats): void {
    const s = this.stats;
    if (s.fall > 0) {
      this.fireFalling(host, dmg, area, ps);
      return;
    }
    const target = host.nearestEnemy(host.px, host.py, AIM_RANGE);
    let ax = host.faceX;
    let ay = host.faceY;
    if (target) {
      const d = Math.hypot(target.x - host.px, target.y - host.py) || 1;
      ax = (target.x - host.px) / d;
      ay = (target.y - host.py) / d;
    }
    const baseAngle = Math.atan2(ay, ax);
    const speed = s.speed * ps.projectileSpeedMul;
    const st = this.outStatus(ps);
    const boom = s.explodeRadius * area;
    for (let i = 0; i < s.count; i++) {
      const a = baseAngle + (i - (s.count - 1) / 2) * 0.16;
      host.spawnProjectile({
        x: host.px, y: host.py,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        damage: dmg, pierce: s.pierce, radius: s.size * area,
        knockback: s.knockback, life: 1.3, def: this.def, status: st,
        explodeRadius: boom, explodeDamage: dmg * s.explodeRatio,
        halfWidth: s.width * area, pool: s.pool > 0, lifesteal: s.lifesteal,
      });
    }
    this.cast();
    host.playAttackPose('cast');
  }

  /**
   * 하늘에서 떨어진다(유성우·파편 비·뇌우). 가까운 적들 머리 위로 비스듬히 내리꽂혀 떨어진 자리가 터진다.
   * 떨어진 자리 한가운데(탄 크기 ×2)는 제 피해를 다 받고, 그 둘레는 폭발 피해를 받는다.
   * 노릴 적이 없으면 쿨타임을 쓰지 않고 곧 다시 찾는다.
   */
  private fireFalling(host: WeaponHost, dmg: number, area: number, ps: PlayerStats): void {
    const s = this.stats;
    const targets = host.pickTargets(host.px, host.py, AIM_RANGE, s.count);
    if (targets.length === 0) {
      this.timer = 0.1;
      return;
    }
    const st = this.outStatus(ps);
    const r = s.size * area;
    const boom = Math.max(s.explodeRadius * area, r * 3, 36);
    const ratio = s.explodeRadius > 0 ? s.explodeRatio : 0.6;
    for (let i = 0; i < s.count; i++) {
      const t = targets[i % targets.length];
      const tx = t.x + (host.rng.next() - 0.5) * 40;
      const ty = t.y + (host.rng.next() - 0.5) * 40;
      const T = 0.38 + i * 0.07; // 여러 발이면 차례로 떨어진다
      const sx = tx + 110;
      const sy = ty - 420;
      host.spawnProjectile({
        x: sx, y: sy, vx: (tx - sx) / T, vy: (ty - sy) / T,
        damage: dmg, pierce: 0, radius: r, knockback: s.knockback, life: T, def: this.def, status: st,
        explodeRadius: boom, explodeDamage: dmg * ratio, fall: true, pool: s.pool > 0, lifesteal: s.lifesteal,
      });
    }
    this.cast();
    host.playAttackPose('cast');
  }

  // 사방으로 파편. cross 면 십자(+) 방향으로 고정해서 쏜다(가스 압착 초반).
  private fireNova(host: WeaponHost, dmg: number, area: number, ps: PlayerStats): void {
    const s = this.stats;
    const speed = s.speed * ps.projectileSpeedMul;
    const start = s.cross > 0 ? 0 : host.rng.next() * Math.PI * 2;
    const st = this.outStatus(ps);
    const boom = s.explodeRadius * area;
    for (let i = 0; i < s.count; i++) {
      const a = start + (Math.PI * 2 * i) / s.count;
      host.spawnProjectile({
        x: host.px, y: host.py,
        vx: Math.cos(a) * speed, vy: Math.sin(a) * speed,
        damage: dmg, pierce: s.pierce, radius: s.size * area,
        knockback: s.knockback, life: 1.6, def: this.def, status: st,
        explodeRadius: boom, explodeDamage: dmg * s.explodeRatio,
        halfWidth: s.width * area, lifesteal: s.lifesteal,
      });
    }
    this.cast();
    host.playAttackPose('cast');
  }

  // 발밑에 장판. follow 면 장판이 플레이어를 따라다닌다(풍화설영의 눈보라).
  private dropFields(host: WeaponHost, dmg: number, area: number): void {
    const s = this.stats;
    const st = this.outStatus(host.playerStats);
    const follow = s.follow > 0;
    const opts = {
      follow,
      guard: s.guard,
      beam: s.beam,
      // 황금 광선 한 줄기 = 장판 한 번 피해의 36배. 광선은 보스부터 쳐서 보스전이 다른 고유의 1.7배였다 — 2026-09-22 40 → 36 (카드 숫자는 그대로)
      beamDamage: dmg * 36,
      heal: s.zoneHeal,
    };
    for (let i = 0; i < s.count; i++) {
      const a = host.rng.next() * Math.PI * 2;
      const r = i === 0 || follow ? 0 : host.rng.range(30, 90);
      host.spawnField(
        host.px + Math.cos(a) * r, host.py + Math.sin(a) * r,
        s.size * area, dmg, s.duration, s.slow, this.def, st, opts,
      );
    }
    // 크게 가끔 나오는 장판은 펼쳐질 때 한 번 퍼지는 고리로 알린다
    if (this.look.fieldBurst) host.burst(host.px, host.py, s.size * area, this.def.attr, this.look.fieldBurst, this.ramp);
    this.cast();
  }

  /**
   * 전류: 가장 가까운 적을 때리고 주변으로 옮겨붙는다.
   * 첫 대상은 옮겨붙는 거리보다 멀리서도 잡는다(쏘는 기술이다). 잡을 적이 없으면
   * 쿨타임을 쓰지 않고 0.1초 뒤에 다시 찾는다 — 예전에는 적이 드문 초반에 절반 넘게 헛돌았다.
   */
  private fireChain(host: WeaponHost, dmg: number, area: number): void {
    const s = this.stats;
    const jump = s.radius * area;
    const exclude = new Set<number>();
    let hit = 0;
    // 여러 줄기면 줄기마다 서로 다른 적에서 시작한다
    for (let v = 0; v < s.volley; v++) {
      hit += host.chainDamage({
        x: host.px, y: host.py,
        firstRange: Math.max(jump * 1.8, 280), jumpRange: jump,
        damage: dmg, jumps: s.count, knockback: s.knockback,
        status: this.outStatus(host.playerStats), splash: s.size * area,
        style: this.look.chain ?? 'arc', splashStyle: this.look.burst ?? 'ring', splashRamp: this.ramp,
        exclude, bolt: s.bolt,
      });
    }
    if (hit > 0) {
      // 전류는 옮겨붙은 수가 많을수록 길고 큰 소리를 낸다 (audio.ts 의 arc 가 hops 를 본다).
      // 굵은 번개(bolt 2 이상)는 늘 가장 긴 소리를 낮고 크게 — "콰직".
      const heavy = s.bolt >= 2;
      this.cast(heavy ? { hops: Math.max(hit, 10), level: this.level, gain: 2, rate: 0.84 } : { hops: hit, level: this.level });
      this.hit(host);
      host.playAttackPose('cast');
    } else {
      this.timer = 0.1;
    }
  }

  // ── 액티브 (도약) ──────────────────────────────────────

  /** 액티브 스킬인가 (Space/E 로 쓴다) — 사냥 준비의 도약, 회광반조의 변신 */
  get isActive(): boolean {
    return this.def.behavior === 'leap' || this.toggleForm;
  }

  /** Space 로 켜고 끄는 변신 스킬인가 (회광반조). formTime 이 0 이면 예전처럼 가진 동안 늘 변신 */
  get toggleForm(): boolean {
    return !!this.def.form && this.stats.formTime > 0;
  }

  /** 지금 이 무기의 모습(def.form)이어야 하는가 */
  get formActive(): boolean {
    return !!this.def.form && (this.stats.formTime <= 0 || this.formOn);
  }

  /** 기력 0~1 (변신 스킬이 아니면 1) */
  get formVigor(): number {
    return this.vigor;
  }

  /** 지쳐서 기력 회복이 멈춰 있는가 */
  get formTired(): boolean {
    return this.tiredT > 0;
  }

  /** 액티브 스킬이면 HUD 에 그릴 상태. ratio 는 다음 한 번이 차는 진행도(0~1, 가득 차 있으면 1). */
  get activeState(): ActiveState | null {
    if (this.toggleForm) {
      const ready = !this.formOn && this.vigor >= 1;
      return { kind: 'form', charges: ready ? 1 : 0, max: 1, ratio: this.vigor, combo: 0, on: this.formOn, tired: this.tiredT > 0 };
    }
    if (this.def.behavior !== 'leap') return null;
    const max = this.stats.charges;
    const charges = this.charges < 0 ? max : this.charges;
    const ratio = charges >= max ? 1 : 1 - Math.max(0, this.rechargeT) / Math.max(0.05, this.lastCd);
    // 연속 사냥 창이 열려 있으면 다음 도약이 몇 단째인지 (0 = 연속 아님)
    return { kind: 'leap', charges, max, ratio, combo: this.comboT > 0 ? Math.min(2, this.combo + 1) : 0, on: false, tired: false };
  }

  /** 도약: 쓸 때까지 가만히 충전만 한다. 시뮬레이터에서는 적이 가장 두꺼운 쪽으로 알아서 뛴다. */
  private updateLeap(host: WeaponHost, dt: number, cd: number): void {
    this.lastCd = cd;
    const max = this.stats.charges;
    if (this.charges < 0) this.charges = max;
    if (this.charges > max) this.charges = max;
    if (this.charges < max) {
      this.rechargeT -= dt;
      if (this.rechargeT <= 0) {
        this.charges++;
        this.rechargeT = this.charges < max ? cd : 0;
      }
    }
    if (this.comboT > 0) {
      this.comboT -= dt;
      if (this.comboT <= 0) this.combo = 0;
    }
    if (host.autoActive && this.charges > 0 && !host.dashing) {
      const spot = host.densestSpot(host.px, host.py, 620);
      if (spot) this.activate(host, spot.x - host.px, spot.y - host.py);
    }
  }

  /**
   * 액티브 스킬을 쓴다. 변신이면 켜고 끈다(기력이 가득일 때만 켠다). 도약이면 (dx, dy) 쪽으로 — 길이가 0이면 바라보는 쪽.
   * 쓸 수 없으면 false.
   */
  activate(host: WeaponHost, dx: number, dy: number): boolean {
    if (this.toggleForm) {
      if (this.formOn) {
        this.setForm(false, 'cancel');
        return true;
      }
      if (this.vigor < 1 || this.formHold === 'off') return false;
      this.setForm(true);
      return true;
    }
    if (this.def.behavior !== 'leap') return false;
    if (this.charges < 0) this.charges = this.stats.charges;
    if (this.charges <= 0 || host.dashing) return false;
    let len = Math.hypot(dx, dy);
    if (len < 1e-3) {
      dx = host.faceX;
      dy = host.faceY;
      len = Math.hypot(dx, dy) || 1;
    }
    const ps = host.playerStats;
    const s = this.stats;
    const area = s.area * ps.areaMul;
    // 연속 사냥: 착지하고 1.5초 안에 또 뛰면 한 단씩 세진다(피해 +50%씩, 3단까지). 착지 자리도 조금씩 커진다.
    const step = this.comboT > 0 ? Math.min(2, this.combo + 1) : 0;
    const ok = host.leap({
      dirX: dx / len, dirY: dy / len,
      distance: s.leap,
      radius: s.radius * area * (1 + step * 0.15),
      damage: s.damage * ps.damageMul * (1 + step * 0.5),
      knockback: s.knockback,
      status: this.outStatus(ps),
      attr: this.def.attr,
      burst: this.look.burst ?? 'ring',
      ramp: this.ramp,
      burstSfx: this.look.burstSfx,
      onLand: (kills) => {
        this.combo = step;
        this.comboT = 1.5;
        // 착지로 잡은 만큼 다음 도약이 빨리 찬다 (한 마리 0.04초, 한 번에 최대 0.5초)
        if (this.charges < this.stats.charges) this.rechargeT -= Math.min(0.5, kills * 0.04);
      },
    });
    if (!ok) return false;
    if (this.charges >= this.stats.charges) this.rechargeT = this.lastCd;
    this.charges--;
    this.cast();
    host.playAttackPose('cast');
    return true;
  }

  /**
   * 여제의 명령: 콰르릉 — 소리가 먼저 나고, STRIKE_DELAY 뒤 번개가 한 번에 내리꽂힌다.
   * 번개가 닿는 띠(길이 radius, 폭 size×2) 안의 적은 전부 맞는다. 노릴 적이 없으면 쿨타임을 쓰지 않는다.
   */
  private updateStrike(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    const s = this.stats;
    const length = s.radius * area;
    for (let i = 0; i < this.strikes.length; i++) {
      const k = this.strikes[i];
      k.wait -= dt;
      if (k.wait > 0) continue;
      const hits = host.lightningStrike({
        x: host.px, y: host.py, dirX: k.dx, dirY: k.dy,
        length, halfWidth: s.size * area,
        damage: dmg, knockback: s.knockback, status: this.outStatus(host.playerStats),
        storm: s.storm,
      });
      this.drain(host, hits, dmg);
      if (hits > 0) this.hit(host);
      this.strikes.splice(i, 1);
      i--;
    }

    this.timer -= dt;
    if (this.timer > 0) return;
    const target = host.nearestEnemy(host.px, host.py, Math.min(900, length));
    if (!target) {
      this.timer = 0.1;
      return;
    }
    this.timer = cd;
    const base = Math.atan2(target.y - host.py, target.x - host.px);
    const dirs = this.strikeDirs(host, base, s.count, Math.min(900, length), s.size * area);
    dirs.forEach((a, i) => this.strikes.push({ wait: STRIKE_DELAY + i * 0.07, dx: Math.cos(a), dy: Math.sin(a) }));
    this.cast();
    host.playAttackPose('cast');
  }

  /**
   * 여제의 번개 방향. 첫 줄기는 가장 가까운 적에게. 둘째부터는 이미 고른 방향과 60° 넘게 벌어진 쪽 중
   * 띠 안에 적이 가장 많이 드는 방향으로 — 여러 줄기가 한쪽으로만 몰리지 않게(1.16 전에는 전부 가장 가까운 적 쪽으로 20°씩만 벌어졌다).
   * 다른 쪽에 적이 하나도 없을 때만 예전처럼 첫 줄기 옆으로 조금 벌려 쏜다(허공에 버리지 않게).
   */
  private strikeDirs(host: WeaponHost, base: number, count: number, length: number, halfWidth: number): number[] {
    const dirs = [base];
    if (count <= 1) return dirs;
    const sample = host.pickTargets(host.px, host.py, length, 48).slice(); // 고르게 뽑은 적 — 방향마다 몇이 드는지 셀 표본
    const CAND = 24;
    const MIN_SEP = Math.PI / 3;
    const sep = (a: number, b: number): number => Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
    for (let n = 1; n < count; n++) {
      let best = NaN;
      let bestHits = 0;
      for (let c = 1; c < CAND; c++) {
        const a = base + (c / CAND) * Math.PI * 2;
        if (dirs.some((d) => sep(a, d) < MIN_SEP)) continue;
        const dx = Math.cos(a);
        const dy = Math.sin(a);
        let hits = 0;
        for (const e of sample) {
          const rx = e.x - host.px;
          const ry = e.y - host.py;
          const along = rx * dx + ry * dy;
          if (along < 0 || along > length) continue;
          if (Math.abs(ry * dx - rx * dy) > halfWidth + e.radius) continue;
          hits++;
        }
        if (hits > bestHits) {
          bestHits = hits;
          best = a;
        }
      }
      dirs.push(Number.isNaN(best) ? base + (n % 2 === 1 ? 1 : -1) * Math.ceil(n / 2) * 0.36 : best);
    }
    return dirs;
  }

  /**
   * 기사: 거대한 검을 불러낸다 — 검 부딪는 소리와 함께 나타나 주변을 끌어당기고,
   * "스윽"(크게 반 바퀴) → "슥"(되돌아 반 바퀴) → "휭휭"(한 바퀴씩 회전)으로 벤다.
   * 휘두르는 횟수가 count 다. 한 번 휘두를 때 같은 적은 한 번만 맞는다.
   */
  private updateGreatblade(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    const s = this.stats;
    const R = s.radius * area;
    const art = getSwordArt('knight');
    // 그림 검은 한 번만 만들고 크기로 맞춘다. 코드로 찍는 검은 반경이 바뀌면 다시 굽는다
    const key = art ? 'blade:art' : `blade:${Math.round(R)}`;
    if (key !== this.fxKey) {
      this.blade?.destroy();
      this.bladeTex?.destroy(true);
      this.bladeTex = null;
      this.trail?.destroy();
      this.trail = new Graphics();
      host.fxLayer.addChild(this.trail);
      if (art) {
        this.blade = new Sprite(art.texture); // 공용 그림이라 bladeTex 에 안 넣는다(지우면 안 된다)
        this.blade.anchor.set(art.anchorX, art.anchorY);
      } else {
        const sw = giantSword(R);
        this.bladeTex = sw.texture;
        this.blade = new Sprite(sw.texture);
        this.blade.anchor.set(sw.anchorX, sw.anchorY);
      }
      this.blade.visible = false;
      host.fxLayer.addChild(this.blade);
      this.fxKey = key;
      this.bladeT = -1;
    }
    // 칼끝이 판정 반경 R 에 딱 닿게 — 범위(area)·패시브로 R 이 커지면 검도 같이 커진다
    this.bladeK = art ? R / art.length : 1;
    this.bladeIn = art ? Math.max(0.2, (art.guard + 12) / art.length) : 0.35;
    const blade = this.blade!;
    const trail = this.trail!;

    this.timer -= dt;
    if (this.bladeT < 0) {
      if (this.timer > 0) return;
      // 주변에 벨 것이 없으면 부르지 않는다
      if (!host.nearestEnemy(host.px, host.py, R * 1.6)) {
        this.timer = 0.1;
        return;
      }
      this.timer = cd;
      this.startBlade(host);
      audio.play('clash');
      host.playAttackPose('attack');
    }

    this.bladeT += dt;
    const t = this.bladeT;
    const SUMMON = 0.16;
    // 검이 나와 있는 동안 날아든 적 탄은 되받아친다
    if (s.reflect > 0 && host.catchShots(host.px, host.py, R, dmg * 0.6) > 0) audio.play('reflect');
    // 나타나자마자 끌어당긴다
    if (!this.bladePulled && t >= 0.04) {
      this.bladePulled = true;
      host.pullEnemies(host.px, host.py, R * 1.7, R * 0.55);
    }

    const st = this.outStatus(host.playerStats);
    let angle = this.bladePlan[0].from;
    let hits = 0;
    while (this.bladeIdx < this.bladePlan.length) {
      const sw = this.bladePlan[this.bladeIdx];
      if (t < sw.start) break;
      const u = Math.min(1, (t - sw.start) / sw.dur);
      // 베기는 빠르게 들어갔다 끝에서 멈칫, 회전은 고르게
      const e = sw.kind === 'slash' ? 1 - Math.pow(1 - u, 2.2) : u;
      angle = sw.from + (sw.to - sw.from) * e;
      if (this.bladeAngle !== angle) {
        hits += host.sweepDamage(host.px, host.py, this.bladeAngle, angle, R, dmg, s.knockback, st, this.bladeSeen);
      }
      this.bladeAngle = angle;
      if (u < 1) break;
      // 다음 휘두르기로
      this.bladeIdx++;
      this.bladeSeen.clear();
      const nx = this.bladePlan[this.bladeIdx];
      if (nx) {
        this.bladeAngle = nx.from;
        this.swingSound(nx);
      }
    }
    if (hits > 0) {
      this.drain(host, hits, dmg);
      this.hit(host);
    }

    // 그리기
    const done = this.bladeIdx >= this.bladePlan.length;
    const last = this.bladePlan[this.bladePlan.length - 1];
    const endT = last.start + last.dur;
    blade.visible = true;
    blade.position.set(Math.round(host.px), Math.round(host.py));
    blade.rotation = this.bladeAngle;
    blade.alpha = t < SUMMON ? t / SUMMON : done ? Math.max(0, 1 - (t - endT) / 0.14) : 1;
    blade.scale.set(this.bladeK * (t < SUMMON ? 0.6 + 0.4 * (t / SUMMON) : 1));
    this.trailPts.push({ a: this.bladeAngle, t });
    while (this.trailPts.length && t - this.trailPts[0].t > 0.12) this.trailPts.shift();
    this.drawTrail(trail, host.px, host.py, R, t);

    if (done && t - endT > 0.14) {
      this.bladeT = -1;
      blade.visible = false;
      trail.clear();
      this.trailPts.length = 0;
    }
  }

  private startBlade(host: WeaponHost): void {
    const face = Math.atan2(host.faceY, host.faceX);
    const half = (115 * Math.PI) / 180;
    const plan: BladeSweep[] = [];
    let t = 0.16; // 소환에 걸리는 시간
    const n = Math.max(1, this.stats.count);
    let a = face - half;
    let dir = 1;
    for (let i = 0; i < n; i++) {
      if (i < 2) {
        // 스윽 / 슥 — 크게 반 바퀴 남짓, 두 번째는 되돌아 벤다
        const dur = i === 0 ? 0.26 : 0.2;
        plan.push({ start: t, dur, from: a, to: a + dir * 2 * half, kind: 'slash' });
        a += dir * 2 * half;
        dir = -dir;
        t += dur + 0.06;
      } else {
        // 휭휭 — 한 바퀴씩, 쉬지 않고
        plan.push({ start: t, dur: 0.2, from: a, to: a - dir * Math.PI * 2, kind: 'spin' });
        a -= dir * Math.PI * 2;
        t += 0.2;
      }
    }
    this.bladePlan = plan;
    this.bladeIdx = 0;
    this.bladeT = 0;
    this.bladeAngle = plan[0].from;
    this.bladeSeen.clear();
    this.bladePulled = false;
    this.trailPts.length = 0;
    this.swingSound(plan[0]);
  }

  /**
   * 회광반조: 잿빛대검을 크게 휘두른다. count 번 — 앞, 뒤, 양옆 차례로, 번갈아 반대 방향으로 쓴다.
   * 칼날이 지나간 부채꼴의 적을 베고(한 번 휘두를 때 한 번씩), 그 호를 따라 바닥에 검상이 "휘이익" 난다.
   * 칼끝이 판정 반경 R 에 닿게 그림 크기를 맞춘다(범위가 커지면 검도 커진다).
   */
  /** 변신을 켜고 끈다. 켜면 FORM_IGNITE 뒤 둘레가 터지고, 변신 동작이 끝난 뒤부터 휘두른다 */
  private setForm(on: boolean, why: 'tired' | 'cancel' = 'cancel'): void {
    if (on === this.formOn) return;
    this.formOn = on;
    this.formIntro = false;
    if (on) {
      this.igniteT = FORM_IGNITE;
      this.tiredT = 0;
    } else {
      this.formEnd = why;
      this.tiredT = why === 'tired' ? FORM_TIRED : 0;
      this.igniteT = -1;
      this.timer = Math.max(this.timer, 1.2); // 풀리는 동작(움츠러듦·식음) 동안은 잔불도 쉰다
      // 불이 꺼지면 휘두르던 대검도 사라진다
      this.bladeT = -1;
      if (this.blade) this.blade.visible = false;
      this.trail?.clear();
      this.trailPts.length = 0;
    }
  }

  /**
   * 회광반조 기력: 변신한 동안 formTime 초에 걸쳐 닳고, 바닥나면 지쳐서 풀린다(FORM_TIRED 동안 회복 멈춤).
   * 변신하지 않은 동안은 formRecover 초에 걸쳐 가득 찬다. 처음 얻은 순간은 한 번 저절로 변신한다.
   */
  private updateVigor(host: WeaponHost, dt: number, dmg: number, area: number): void {
    const s = this.stats;
    if (this.formHold === 'on') {
      this.vigor = 1;
      if (!this.formOn) this.setForm(true);
    } else if (this.formHold === 'off' && this.formOn) {
      this.setForm(false);
    }
    if (this.formOn) {
      if (this.formHold !== 'on') this.vigor -= dt / s.formTime;
      if (this.vigor <= 0) {
        this.vigor = 0;
        this.setForm(false, 'tired');
      }
    } else if (this.tiredT > 0) {
      this.tiredT -= dt;
    } else if (this.vigor < 1) {
      this.vigor = Math.min(1, this.vigor + dt / Math.max(1, s.formRecover));
      if (this.vigor >= 1 && !host.autoActive) audio.play('chime', { gain: 1.4, rate: 0.8 }); // 다시 변신할 수 있다
    }
    // 처음 얻은 순간 / 측정·시뮬레이션(autoActive)은 기력이 차면 알아서 변신한다
    if (!this.formOn && this.vigor >= 1 && this.formHold !== 'off') {
      if (this.formIntro || (host.autoActive && host.nearestEnemy(host.px, host.py, 420))) this.setForm(true);
    }
    this.formIntro = false;
    // 변신 폭발: 몸이 검붉게 물든 뒤 둘레가 한 번 터진다
    if (this.igniteT >= 0) {
      this.igniteT -= dt;
      if (this.igniteT < 0 && this.formOn) {
        const R = s.radius * area * 1.1;
        const hits = host.areaDamage(host.px, host.py, R, dmg * s.formBurst, 360, 0, 0, -1, 0, this.outStatus(host.playerStats));
        host.burst(host.px, host.py, R, this.def.attr, 'fire', this.ramp);
        host.heavyHit();
        this.drain(host, hits, dmg);
      }
    }
  }

  private updateAshblade(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    const s = this.stats;
    // 회광반조(Space 변신): 변신한 동안만 제 힘, 아니면 잔불 — 느리게 한 번씩, 검상·점화 없이
    let ember = false;
    if (this.toggleForm) {
      this.updateVigor(host, dt, dmg, area);
      if (!this.formOn) {
        if (s.ember <= 0) {
          if (this.blade) this.blade.visible = false;
          this.trail?.clear();
          this.bladeT = -1;
          return;
        }
        ember = true;
      } else if (host.transforming && this.bladeT < 0) {
        return; // 변신하는 동작이 끝난 뒤부터 휘두른다
      }
    }
    if (ember) {
      dmg *= s.ember;
      cd *= EMBER_CD;
    }
    const R = s.radius * area * (ember ? EMBER_REACH : 1);
    const art = getSwordArt('ash');
    const key = art ? 'ash:art' : `ash:${Math.round(R)}`;
    if (key !== this.fxKey) {
      this.blade?.destroy();
      this.bladeTex?.destroy(true);
      this.bladeTex = null;
      this.trail?.destroy();
      this.trail = new Graphics();
      host.fxLayer.addChild(this.trail);
      if (art) {
        this.blade = new Sprite(art.texture);
        this.blade.anchor.set(art.anchorX, art.anchorY);
      } else {
        const sw = giantSword(R);
        this.bladeTex = sw.texture;
        this.blade = new Sprite(sw.texture);
        this.blade.anchor.set(sw.anchorX, sw.anchorY);
        this.blade.tint = 0x6a4a4a;
      }
      this.blade.visible = false;
      host.fxLayer.addChild(this.blade);
      this.fxKey = key;
      this.bladeT = -1;
    }
    this.bladeK = art ? R / art.length : 1;
    this.bladeIn = art ? Math.max(0.2, (art.guard + 12) / art.length) : 0.3;
    const blade = this.blade!;
    const trail = this.trail!;

    this.timer -= dt;
    if (this.bladeT < 0) {
      if (this.timer > 0) return;
      if (!host.nearestEnemy(host.px, host.py, R * 1.3)) {
        this.timer = 0.1;
        return;
      }
      this.timer = cd;
      // 휘두르기 계획: 앞 → 뒤 → 옆 → 옆, 번갈아 반대 방향으로 쓴다 (잔불은 앞으로 한 번)
      const face = Math.atan2(host.faceY, host.faceX);
      const half = (s.arc * Math.PI) / 360;
      const dirs = [0, Math.PI, Math.PI / 2, -Math.PI / 2];
      this.bladePlan = [];
      for (let i = 0; i < (ember ? 1 : Math.max(1, s.count)); i++) {
        const c = face + dirs[i % 4];
        const sg = i % 2 === 0 ? 1 : -1;
        this.bladePlan.push({ start: i * (ASH_SWEEP + ASH_GAP), dur: ASH_SWEEP, from: c - half * sg, to: c + half * sg, kind: 'slash' });
      }
      this.bladeIdx = -1;
      this.bladeT = 0;
      this.bladeAngle = this.bladePlan[0].from;
      this.trailPts.length = 0;
    }

    this.bladeT += dt;
    const t = this.bladeT;
    // 잔불은 [화염]만 — 점화(10 쌓이면 터짐)와 꺼지지 않는 불은 변신해야
    const full = this.outStatus(host.playerStats);
    const st = ember ? { burn: full.burn, burnDmg: full.burnDmg } : full;
    let hits = 0;
    const plan = this.bladePlan;
    // 다음 휘두르기로 넘어갈 때: 자세·소리·검상
    while (this.bladeIdx + 1 < plan.length && t >= plan[this.bladeIdx + 1].start) {
      this.bladeIdx++;
      const sw = plan[this.bladeIdx];
      this.bladeAngle = sw.from;
      this.bladeSeen.clear();
      this.trailPts.length = 0;
      this.cast(ember ? { rate: 0.9, gain: 0.55 } : { rate: this.bladeIdx % 2 === 0 ? 1 : 1.06 });
      host.playAttackPose('attack');
      if (s.trail > 0 && !ember) {
        const burn = { burn: 2 + Math.floor((this.status.burn ?? 0) * 0.5), burnDmg: this.burnDmg || undefined };
        host.slashWound(host.px, host.py, R * 0.62, sw.from, sw.to, ASH_SWEEP, dmg * 0.14, burn);
      }
    }
    const sw = plan[Math.max(0, this.bladeIdx)];
    const u = Math.min(1, Math.max(0, (t - sw.start) / sw.dur));
    const e = 1 - Math.pow(1 - u, 2.4); // 확 들어갔다 끝에서 멈칫
    const angle = sw.from + (sw.to - sw.from) * e;
    if (this.bladeIdx >= 0 && angle !== this.bladeAngle) {
      hits += host.sweepDamage(host.px, host.py, this.bladeAngle, angle, R, dmg, s.knockback, st, this.bladeSeen);
    }
    this.bladeAngle = angle;
    if (hits > 0) {
      this.drain(host, hits, dmg);
      this.hit(host);
    }

    // 그리기: 휘두르는 동안 보이고, 마지막 휘두르기 뒤 0.12초에 걸쳐 사라진다
    const last = plan[plan.length - 1];
    const endT = last.start + last.dur;
    blade.visible = true;
    blade.position.set(Math.round(host.px), Math.round(host.py));
    blade.rotation = this.bladeAngle;
    const pop = u < 0.2 ? 0.8 + u : 1;
    blade.scale.set(this.bladeK * pop);
    // 잔불: 빛이 죽은 잿빛 검 (그림이 없으면 코드로 찍은 검을 더 어둡게)
    blade.tint = ember ? (art ? 0x8c7c78 : 0x4a3a3a) : art ? 0xffffff : 0x6a4a4a;
    blade.alpha = (t > endT ? Math.max(0, 1 - (t - endT) / 0.12) : 1) * (ember ? 0.8 : 1);
    this.trailPts.push({ a: this.bladeAngle, t });
    while (this.trailPts.length && t - this.trailPts[0].t > 0.1) this.trailPts.shift();
    // 칼끝 쪽만 가는 궤적 — 넓은 부채꼴로 칠하면 검상·검 그림보다 튀어서 싸 보였다
    if (ember) this.drawTrail(trail, host.px, host.py, R, t, 0x3a2a2a, 0x9a6a5a, 0.72, 0.86, 0.2);
    else this.drawTrail(trail, host.px, host.py, R, t, 0x6a0c14, 0xff5a3a, 0.72, 0.86, 0.35);
    if (t - endT > 0.12) {
      this.bladeT = -1;
      blade.visible = false;
      trail.clear();
      this.trailPts.length = 0;
    }
  }

  /** 소한공: 내지를 손 (방향은 첫 손을 낼 때 정한다) */
  private palms: Array<{ wait: number; dx: number; dy: number; idx: number }> = [];

  /**
   * 【비급】 소한공 — 가까운 적 쪽으로 손을 count 번 내지른다(PALM_GAP 간격).
   * 첫 손 [1] 응상: 짧은 띠(쇄상의 65%)에 피해와 [서리]. 다음 손부터 [2] 쇄상: 긴 띠에서 적마다 [서리]를 2 녹여
   * 녹인 1 마다 피해 +50%, 그리고 [서리]를 다시 입힌다. 이 [서리]로는 얼리지 못한다(chill).
   * 노릴 적이 없으면 쿨타임을 쓰지 않고 곧 다시 찾는다.
   */
  private updatePalm(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    const s = this.stats;
    const L = s.radius * area;
    const hw = s.size * area;
    const st = this.outStatus(host.playerStats);
    for (let i = 0; i < this.palms.length; i++) {
      const p = this.palms[i];
      p.wait -= dt;
      if (p.wait > 0) continue;
      const first = p.idx === 0;
      const hits = first
        ? host.palmStrike(host.px, host.py, p.dx, p.dy, L * 0.65, hw, dmg, s.knockback, st, 0, 0, false)
        : host.palmStrike(host.px, host.py, p.dx, p.dy, L, hw * 1.25, dmg * 1.2, s.knockback * 1.4, st, 2, 0.5, true);
      this.cast({ rate: first ? 1 : 0.85 });
      host.playAttackPose('attack');
      if (hits > 0) {
        this.drain(host, hits, dmg);
        this.hit(host);
      }
      this.palms.splice(i, 1);
      i--;
    }
    this.timer -= dt;
    if (this.timer > 0) return;
    const t = host.nearestEnemy(host.px, host.py, L * 1.2);
    if (!t) {
      this.timer = 0.1;
      return;
    }
    this.timer = cd;
    const d = Math.hypot(t.x - host.px, t.y - host.py) || 1;
    const dx = (t.x - host.px) / d;
    const dy = (t.y - host.py) / d;
    for (let k = 0; k < Math.max(1, s.count); k++) this.palms.push({ wait: k * PALM_GAP, dx, dy, idx: k });
  }

  private swingSound(sw: BladeSweep): void {
    audio.play(sw.kind === 'slash' ? 'bladeSlash' : 'bladeSpin');
  }

  /**
   * 칼날이 지나간 자리에 빛 띠. 조각을 따로 그리면 이음매가 부채살처럼 보여서
   * 지나온 각도 전체를 한 덩어리로 두 겹(넓고 옅게 / 최근 것만 밝게) 그린다. 더하기 합성으로 빛나게.
   */
  private drawTrail(
    g: Graphics, x: number, y: number, R: number, now: number,
    dim = 0x34507a, bright = 0x8fbfff, dimIn = this.bladeIn, brightIn = 0.6, dimAlpha = 0.6,
  ): void {
    g.clear();
    g.blendMode = 'add';
    const pts = this.trailPts;
    if (pts.length < 2) return;
    const band = (from: number, rIn: number, color: number, alpha: number): void => {
      const seg = pts.slice(from);
      if (seg.length < 2) return;
      const path: number[] = [];
      for (const p of seg) path.push(x + Math.cos(p.a) * R, y + Math.sin(p.a) * R);
      for (let i = seg.length - 1; i >= 0; i--) path.push(x + Math.cos(seg[i].a) * rIn, y + Math.sin(seg[i].a) * rIn);
      g.poly(path).fill({ color, alpha });
    };
    const fresh = pts.findIndex((p) => now - p.t < 0.05);
    band(0, R * dimIn, dim, dimAlpha);
    band(Math.max(0, fresh), R * brightIn, bright, 0.4);
  }

  /**
   * 주위를 도는 정령.
   * 다중 소환 유지(fx 'spirit')는 불·얼음·번개(자색) 정령이 차례로 돈다 — 원소마다 거는 상태효과가 다르다([화염]/[서리]/[감전]).
   * tier 1 이상이면 고위 정령(그림이 크고 자세하다). king 이 0보다 크면 도는 정령은 없다 — 고위 정령들이 모여 정령왕 셋이 되고,
   * 정령왕만 캐릭터 곁을 떠다니며 싸운다(spirits.ts).
   */
  private updateOrbit(host: WeaponHost, dt: number, dmg: number, area: number, cd: number, slot: number): void {
    const s = this.stats;
    if (s.king > 0 && SpiritCourt.available()) {
      if (this.nodes.length > 0) {
        for (const n of this.nodes) n.destroy();
        this.nodes = [];
        this.fxKey = '';
      }
      if (!this.court) this.court = new SpiritCourt(host, this.def.fx === 'spirit' ? s.count : 0);
      this.court.burnDmg = this.burnDmg;
      this.court.update(host, dt, dmg * s.king);
      return;
    }
    if (this.court) {
      this.court.destroy();
      this.court = null;
    }
    const size = s.size * area;
    const elemental = this.def.fx === 'spirit';
    const upright = orbitKeepsUpright(this.def.fx);
    this.ensureOrbitFx(host, size);

    this.orbitAngle += s.speed * dt;
    this.nodeT += dt;
    const radius = s.radius * area;
    const st = this.outStatus(host.playerStats);
    // 원소마다 거는 상태효과: 불 [화염] · 얼음 [서리] · 번개 [감전]
    const byEl: StatusApply[] | null = elemental
      ? [{ burn: st.burn, burnLock: st.burnLock, burnDmg: st.burnDmg }, { frost: st.frost }, { shock: 1 }]
      : null;

    // 많으면 안쪽·바깥쪽 두 겹으로 나눠 돈다(뭉쳐서 한 점처럼 보이는 걸 막는다). 원소 정령은 크게 그려서 열까지 한 겹
    const twoRings = this.nodes.length > (elemental ? 10 : 6);
    const half = Math.ceil(this.nodes.length / 2);
    let hits = 0;
    for (let i = 0; i < this.nodes.length; i++) {
      const inner = twoRings && i >= half;
      const ringN = twoRings ? (inner ? this.nodes.length - half : half) : this.nodes.length;
      const ringI = inner ? i - half : i;
      const r = twoRings ? (inner ? radius * 0.58 : radius) : radius;
      const a = this.orbitAngle * (inner ? -1.25 : 1) + (Math.PI * 2 * ringI) / ringN;
      const x = host.px + Math.cos(a) * r;
      const y = host.py + Math.sin(a) * r;
      const sp = this.nodes[i];
      sp.position.set(Math.round(x), Math.round(y));
      if (this.nodeArt) {
        // 개체마다 박자를 어긋나게 해서 한꺼번에 일렁이지 않게
        const fr = this.nodeArt[i % 3];
        applyFrame(sp, fr[Math.floor(this.nodeT * 8 + i * 1.7) % fr.length]);
        sp.rotation = 0;
      } else {
        if (this.nodeFrames.length > 1) sp.texture = this.nodeFrames[Math.floor(this.nodeT * 10 + i * 1.7) % this.nodeFrames.length];
        if (upright) {
          sp.rotation = 0;
          // 그림을 못 읽었을 때: 원소 색으로 물들인다
          if (elemental) sp.tint = [0xffc890, 0xa8d8ff, 0xd0b8ff][i % 3];
        } else sp.rotation = a;
      }
      const hit = host.areaDamage(x, y, size, dmg, s.knockback, 0, 0, slot, cd, byEl ? byEl[i % 3] : st);
      hits += hit;
      this.drain(host, hit, dmg);
    }
    if (hits > 0) this.hit(host);
  }

  /** 도는 것들(정령)의 그림을 지금 수·크기·등급에 맞게 굽는다 */
  private ensureOrbitFx(host: WeaponHost, size: number): void {
    const s = this.stats;
    const elemental = this.def.fx === 'spirit';
    const tier = s.tier > 0 ? 'high' : 'low';
    const key = `orbit:${s.count}:${Math.round(size)}:${tier}`;
    if (key === this.fxKey) return;
    for (const n of this.nodes) n.destroy();
    this.nodes = [];
    for (const t of this.nodeFrames) t.destroy(true);
    this.nodeFrames = [];
    this.nodeArt = null;
    const art = elemental ? ELEMENTS.map((el) => spiritFrames(tier, el)) : null;
    if (art && art.every((f) => f)) this.nodeArt = art as ArtFrame[][];
    else this.nodeFrames = orbitFrames(this.def.fx, size * 0.7, this.ramp);
    for (let i = 0; i < s.count; i++) {
      const sp = new Sprite(this.nodeArt ? this.nodeArt[i % 3][0].texture : this.nodeFrames[0]);
      sp.anchor.set(0.5);
      host.fxLayer.addChild(sp);
      this.nodes.push(sp);
    }
    this.fxKey = key;
  }

  /**
   * 광풍: 불·얼음·번개·모래 회오리 넷이 몸 둘레를 휘몰아치고, pulse 초마다 모여 한꺼번에 터진다(tempest.ts).
   * 궤도 radius · 회오리 크기 size 는 범위 배율을 받고, 한 적은 cooldown 초에 한 번만 맞는다(넷이 같은 칸).
   */
  private updateTempest(host: WeaponHost, dt: number, dmg: number, area: number, cd: number, slot: number): void {
    const s = this.stats;
    if (!this.storm) this.storm = new TempestStorm(host);
    const hits = this.storm.update(host, dt, {
      dmg, cd, slot, radius: s.radius * area, size: s.size * area, knockback: s.knockback, pulse: s.pulse,
      status: this.outStatus(host.playerStats),
    });
    this.drain(host, hits, dmg);
    if (hits > 0) this.hit(host);
  }

  // 적이 가장 두꺼운 곳으로 뛰어들어 착지 지점을 터뜨린다
  private updateBlink(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = cd;

    const spot = host.densestSpot(host.px, host.py, 620);
    if (!spot) return;
    const r = this.stats.radius * area;
    const fromX = host.px;
    const fromY = host.py;
    this.cast();
    host.teleport(spot.x, spot.y);
    host.streak(fromX, fromY, spot.x, spot.y, 0xff8fa3);
    const hit = host.areaDamage(
      spot.x, spot.y, r, dmg, this.stats.knockback, 0, 0, -1, 0, this.outStatus(host.playerStats),
    );
    this.drain(host, hit, dmg);
    host.burst(spot.x, spot.y, r, this.def.attr, this.look.burst ?? 'ring', this.ramp);
    if (this.look.burstSfx) audio.play(this.look.burstSfx);
    host.playAttackPose('cast');
  }

  /** 오라 그림을 지금 반경에 맞게 굽는다. 열쇠는 기본 반경(drawR) — 타이탄이 커지는 것은 굽지 않고 크기만 늘린다 */
  private ensureAuraFx(host: WeaponHost, drawR: number): void {
    const key = `aura:${Math.round(drawR)}`;
    if (key === this.fxKey) return;
    this.aura?.destroy();
    destroyFrames(this.auraFrames);
    // (예전에는 커진 반경으로 굽고 거기에 또 커진 배율을 곱해서, 커지는 중에 레벨업하면 오라 그림이 판정보다 크게 보였다)
    this.auraFrames = auraFrames(this.def.fx, drawR, this.ramp);
    this.aura = new Sprite(this.auraFrames.textures[0]);
    this.aura.anchor.set(this.auraFrames.anchorX, this.auraFrames.anchorY);
    this.aura.alpha = this.def.fx === 'ring' ? 0.7 : 0.9;
    host.fxLayer.addChildAt(this.aura, 0);
    this.fxKey = key;
  }

  // 내 주변 지속 피해
  private updateAura(host: WeaponHost, dt: number, dmg: number, area: number, cd: number, slot: number): void {
    const s = this.stats;
    const drawR = s.radius * area;
    // 타이탄 거신화: 가진 뒤로 잡을수록 몸과 범위가 커진다(최대 1 + grow 배).
    // 그림은 다시 굽지 않고 크기만 늘린다 — 커질 때마다 구우면 그때마다 화면이 멈칫했다(한 번에 150ms 넘게)
    let grow = 1;
    if (s.grow > 0) {
      if (this.killBase < 0) this.killBase = host.kills;
      grow = 1 + Math.min(s.grow, (host.kills - this.killBase) * 0.0002);
      host.setBodyScale(grow);
    }
    const radius = drawR * grow;
    if (s.overheal > 0) host.setOverheal(s.overheal, dmg * 8, this.outStatus(host.playerStats));
    // 그림은 기본 반경으로 굽고, 타이탄이 커진 몫은 아래에서 크기(scale)로만 늘린다
    this.ensureAuraFx(host, drawR);
    if (!this.aura || !this.auraFrames) return;

    this.aura.position.set(Math.round(host.px), Math.round(host.py));
    this.aura.scale.set(grow);
    this.auraT = (this.auraT + dt) % cd;
    const idx = Math.min(
      this.auraFrames.textures.length - 1,
      Math.floor((this.auraT / cd) * this.auraFrames.textures.length),
    );
    this.aura.texture = this.auraFrames.textures[idx];

    const hit = host.areaDamage(host.px, host.py, radius, dmg, s.knockback, 0, 0, slot, cd, this.outStatus(host.playerStats));
    this.drain(host, hit, dmg);
    if (hit > 0) this.hit(host);

    // 타이탄: pulse 초마다 땅을 굴러 기절 충격파
    if (s.pulse > 0) {
      this.pulseT -= dt;
      if (this.pulseT <= 0) {
        this.pulseT = s.pulse;
        const R = radius * 1.35;
        const n = host.areaDamage(host.px, host.py, R, dmg * 2.5, s.knockback * 1.3, 0, 0, -1, 0, { stun: 1.2 });
        // 그림은 커진 정도를 5% 단위로 맞춰 굽는다(판정은 위 R 그대로). 커질 때마다 크기가 조금씩 달라
        // 5초마다 새로 굽느라(10~18ms) 멈칫했다. 단계가 몇 개뿐이라 고유 스킬을 얻을 때 미리 굽는다(warmTextures)
        host.burst(host.px, host.py, drawR * 1.35 * growStep(grow), this.def.attr, 'air', this.ramp);
        host.shakeScreen(0.25, 9);
        audio.play('boom');
        if (n > 0) host.heavyHit();
      }
    }
  }

  /**
   * 이동 방향 앞을 벤다.
   * 여러 번 베는 무기는 한꺼번에 긋지 않고 SWING_GAP 간격으로 차례차례 긋는다.
   * 그래야 "두두!" 하고 두 번 베는 게 눈과 귀로 느껴진다.
   */
  private updateMelee(host: WeaponHost, dt: number, dmg: number, area: number, cd: number): void {
    const s = this.stats;
    const radius = s.radius * area;
    const arc = s.arc;
    this.ensureMeleeFx(host, radius, arc);

    for (const g of this.swings) g.update(dt, host.px, host.py);

    // 뒤따르는 칼질
    for (let i = 0; i < this.pending.length; i++) {
      const p = this.pending[i];
      p.wait -= dt;
      if (p.wait > 0) continue;
      this.swingOnce(host, p.dx, p.dy, p.idx, dmg, radius, arc);
      this.pending.splice(i, 1);
      i--;
    }

    this.timer -= dt;
    if (this.timer > 0) return;
    this.timer = cd;

    // 칼질 사이 간격. 쿨타임이 아주 짧은 무기(광풍 등)는 다음 칼질과 겹치지 않게 좁힌다.
    const gap = s.count > 1 ? Math.min(SWING_GAP, (cd * 0.6) / (s.count - 1)) : 0;
    this.volleyGap = gap;
    const face = Math.atan2(host.faceY, host.faceX);
    for (let i = 0; i < s.count; i++) {
      // 0=정면, 1=후방, 2/3=좌우, 4~7=대각선 넷 — 방향은 첫 칼을 긋는 순간에 정한다.
      // (예전에는 다섯째 칼부터 정면을 또 벴다 — 날바람 「한 방향 더」가 새 방향을 못 벴다)
      const a = face + MELEE_DIRS[i % MELEE_DIRS.length];
      const dx = Math.cos(a);
      const dy = Math.sin(a);
      if (i === 0 || gap <= 0) this.swingOnce(host, dx, dy, i, dmg, radius, arc);
      else this.pending.push({ wait: gap * i, dx, dy, idx: i });
    }
  }

  /** 휘두르는 그림을 지금 크기·각도·횟수에 맞게 굽는다(이미 맞으면 아무것도 안 한다) */
  private ensureMeleeFx(host: WeaponHost, radius: number, arc: number): void {
    const s = this.stats;
    const key = `melee:${s.count}:${Math.round(radius)}:${Math.round(arc)}`;
    if (key === this.fxKey) return;
    for (const g of this.swings) g.destroy();
    this.swings = [];
    destroyFrames(this.swingFrames);
    this.swingFrames = swingFrames(this.def.fx, radius, arc, this.ramp);
    for (let i = 0; i < s.count; i++) this.swings.push(new SwingFx(host.fxLayer, this.swingFrames));
    this.pending = [];
    this.fxKey = key;
  }

  /** 칼질 한 번: 피해, 이펙트, 소리(나갈 때 + 맞았으면 맞는 소리). */
  private swingOnce(host: WeaponHost, dx: number, dy: number, idx: number, dmg: number, radius: number, arc: number): void {
    const s = this.stats;
    const hits = host.coneDamage(host.px, host.py, dx, dy, radius, arc, dmg, s.knockback, this.outStatus(host.playerStats));
    this.swings[idx]?.fire(Math.atan2(dy, dx));
    // 벤 자리에 잿불이 남는다 — 부채꼴 안쪽에 trail 개를 고르게
    if (s.trail > 0) {
      const base = Math.atan2(dy, dx);
      const burn = { burn: 2 + Math.floor((this.status.burn ?? 0) * 0.5), burnDmg: this.burnDmg || undefined };
      for (let k = 0; k < s.trail; k++) {
        const a = base + (s.trail > 1 ? (k / (s.trail - 1) - 0.5) * ((arc * Math.PI) / 180) * 0.7 : 0);
        host.spawnCinder(host.px + Math.cos(a) * radius * 0.62, host.py + Math.sin(a) * radius * 0.62, radius * 0.3, dmg * 0.12, burn);
      }
    }
    this.drain(host, hits, dmg);
    // 두 번째·세 번째 칼은 음을 살짝 바꿔 "같은 소리 반복"이 아니라 연타로 들리게
    const rate = idx === 0 ? 1 : idx === 1 ? 1.07 : idx === 2 ? 0.95 : 1.12;
    // 0.1초보다 촘촘하게 몰아치는 무기(광풍·날바람 만렙)는 한 번의 "휘익"으로 묶는다. 다 내면 드르륵 소음이 된다.
    if (idx === 0 || this.volleyGap >= 0.1) this.cast({ rate });
    if (hits > 0) this.hit(host);
    host.playAttackPose('attack');
  }
}
